import { useTranslation } from '@pancakeswap/localization'
import { Currency, CurrencyAmount, TradeType } from '@pancakeswap/sdk'
import { SWAP_ROUTER_ADDRESSES, SwapRouter } from '@pancakeswap/smart-router/evm'
import { useUserSlippage, useUserSingleHopOnly } from '@pancakeswap/utils/user'
import { SendTransactionResult } from 'wagmi/actions'
import { useCallback, useMemo, useState } from 'react'
import { basisPointsToPercent } from 'utils/exchange'
import { isUserRejected } from 'utils/sentry'
import { transactionErrorToUserReadableMessage } from 'utils/transactionErrorToUserReadableMessage'
import { Address, Hex } from 'viem'
import { useBestAMMTrade } from 'hooks/useBestAMMTrade'
import { useUserSplitRouteEnable, useUserV3SwapEnable } from 'state/user/smartRouter'
import useSendSwapTransaction from 'views/Swap/V3Swap/hooks/useSendSwapTransaction'

interface UseV3AutoSwapExecutionParams {
  account?: Address
  chainId?: number
  swapFromCurrency?: Currency
  swapToCurrency?: Currency
  swapAmount?: CurrencyAmount<Currency>
  enabled?: boolean
}

interface SwapCall {
  address: Address
  calldata: Hex
  value: Hex
}

/**
 * Hook to execute auto-swap before adding liquidity
 *
 * This hook provides:
 * 1. Best trade route using the same logic as swap page
 * 2. Swap router address for approval
 * 3. Execute swap function that follows the exact swap page flow
 *
 * @param params - Swap parameters including currencies and amounts
 * @returns Object with swap router address, trade info, and execute function
 */
export function useV3AutoSwapExecution({
  account,
  chainId,
  swapFromCurrency,
  swapToCurrency,
  swapAmount,
  enabled = true,
}: UseV3AutoSwapExecutionParams) {
  const { t } = useTranslation()
  const [allowedSlippage] = useUserSlippage()
  const [isExecuting, setIsExecuting] = useState(false)

  // Use the same trade fetching logic as swap page
  const [singleHopOnly] = useUserSingleHopOnly()
  const [split] = useUserSplitRouteEnable()
  const [v3Swap] = useUserV3SwapEnable()
  // Fetch best trade using the same hook as swap page
  const {
    isLoading: tradeLoading,
    trade,
    error: tradeError,
  } = useBestAMMTrade({
    amount: enabled ? swapAmount : undefined,
    currency: swapToCurrency,
    baseCurrency: swapFromCurrency,
    tradeType: TradeType.EXACT_INPUT,
    maxHops: singleHopOnly ? 1 : 3,
    maxSplits: split ? 2 : 0,
    v2Swap: false,
    v3Swap,
    stableSwap: false,
    type: 'auto',
  })
  // Get swap router address for approval
  const swapRouterAddress = useMemo(() => {
    return chainId ? SWAP_ROUTER_ADDRESSES[chainId] : undefined
  }, [chainId])

  // Generate swap call parameters using the same logic as swap page
  const swapCalls: SwapCall[] = useMemo(() => {
    if (!trade || !account || !chainId || !swapRouterAddress || !enabled) {
      return []
    }

    try {
      const { value, calldata } = SwapRouter.swapCallParameters(trade, {
        recipient: account,
        slippageTolerance: basisPointsToPercent(allowedSlippage),
        deadlineOrPreviousBlockhash: (Math.floor(Date.now() / 1000) + 1200).toString(), // 20 minutes
      })

      return [
        {
          address: swapRouterAddress,
          calldata,
          value,
        },
      ]
    } catch (error) {
      console.error('Error generating swap call parameters:', error)
      return []
    }
  }, [trade, account, chainId, swapRouterAddress, enabled, allowedSlippage])

  // Use the same swap transaction hook as swap page
  const { callback: swapCallback } = useSendSwapTransaction(account, chainId, trade, swapCalls)

  // Execute swap function - follows the exact same pattern as swap page
  const executeSwap = useCallback(async (): Promise<SendTransactionResult | null> => {
    if (!enabled || !account || !chainId || !swapFromCurrency || !swapToCurrency || !swapAmount || !swapCallback) {
      console.error('Missing required parameters for swap execution')
      return null
    }

    if (!trade) {
      throw new Error('No trade route found')
    }

    setIsExecuting(true)

    try {
      console.log('🔄 Executing auto-swap...', {
        from: swapFromCurrency.symbol,
        to: swapToCurrency.symbol,
        amount: swapAmount.toSignificant(6),
        inputAmount: trade.inputAmount.toSignificant(6),
        outputAmount: trade.outputAmount.toSignificant(6),
      })

      // Execute swap using the same callback as swap page
      const response = await swapCallback()

      console.log('✅ Swap transaction sent:', response.hash)

      setIsExecuting(false)
      return response
    } catch (error) {
      setIsExecuting(false)

      if (isUserRejected(error)) {
        throw new Error(t('Transaction rejected'))
      }

      console.error('❌ Swap failed:', error)
      throw new Error(`Swap failed: ${transactionErrorToUserReadableMessage(error, t)}`)
    }
  }, [enabled, account, chainId, swapFromCurrency, swapToCurrency, swapAmount, swapCallback, trade, t])

  return {
    swapRouterAddress,
    executeSwap,
    isExecuting,
    trade,
    tradeLoading,
    tradeError,
  }
}
